import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Snackbar,
  Typography,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { html as beautifyHtml, css as beautifyCss } from "js-beautify";
import { selectUserToken } from "../../../../../features/userSlice";
import { DesignTemplatesEndPointLogic } from "../../../../../logic/AssetManagement/Design/Templates/DesignTemplatesEndPointLogic";
import SimpleBackdrop from "../../../../BackDrop/BackDrop";
import GeneralCard from "./components/GeneralCard";
import LabelsCard from "./components/LabelsCard";
import CodeCard from "./components/CodeCard";
import AdditionalSettingsCard from "./components/AdditionalSettingsCard";
import CautionNote from "./components/CautionNote";
import RevisionHistoryCard from "./components/RevisionHistoryCard";
import RevisionDetailModal from "./components/RevisionDetailModal";
import { mapTemplateDetails, toEditPayload } from "./apiMappers";
import {
  RevisionContent,
  RevisionFilterState,
  TemplateDetails,
  TemplateRevision,
} from "./types";
import { defaultCustomRange, filterRevisions, rangeToDates } from "./utils";
import { validateTemplate } from "./validation";
import { tokens } from "./tokens";
import GenericEndPointsLogic from "../../../../../logic/GenericEndPointsLogic/GenericEndPointsLogic";

// Static entity type sent to the variants endpoint for this module
const VARIANTS_ENTITY_TYPE = "template";

const buildInitialFilters = (): RevisionFilterState => ({
  range: "7d",
  ...defaultCustomRange(),
  modifiedBy: "",
});

/** Redesigned Edit Template screen (GR-21832). */
const EditTemplateRevision = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const oauthTokenHeader = useSelector(selectUserToken) as any;

  const templateSharedUid = searchParams.get("templateSharedUid") ?? "";
  const websiteSharedUid = searchParams.get("selectedSite") ?? "";

  const { designTemplatesData, siteOptions } = useSelector(
    (state: any) => state.template,
  );

  const [template, setTemplate] = useState<TemplateDetails | null>(null);
  const [revisions, setRevisions] = useState<TemplateRevision[]>([]);
  const [loading, setLoading] = useState(true);
  const [revisionsLoading, setRevisionsLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  /**
   * The form is seeded from the first successful response only. Switching a
   * revision tab re-requests the template for its date range, and must not
   * overwrite edits the user has already made.
   */
  const formHydrated = useRef(false);

  /**
   * Separate from `formHydrated`: only the very first load shows the full-page
   * loader. A reload after saving re-seeds the form but must not blank the
   * screen out.
   */
  const initialLoadDone = useRef(false);

  // Form state, seeded from the API once the template is loaded.
  const [templateName, setTemplateName] = useState("");
  const [labels, setLabels] = useState<string[]>([]);
  const [html, setHtml] = useState("");
  const [css, setCss] = useState("");
  const [additionalSettings, setAdditionalSettings] = useState("");

  const [labelOptions, setLabelOptions] = useState<string[]>([]);
  const [filters, setFilters] =
    useState<RevisionFilterState>(buildInitialFilters);
  const [activeRevision, setActiveRevision] = useState<TemplateRevision | null>(
    null,
  );
  const [modalOpen, setModalOpen] = useState(false);
  const [toast, setToast] = useState<{
    message: string;
    severity: "success" | "error";
  } | null>(null);

  const applyTemplate = useCallback((details: TemplateDetails) => {
    // Revisions refresh on every range change; the rest only on first load.
    setRevisions(details.revisions);

    if (formHydrated.current) return;
    formHydrated.current = true;

    setTemplate(details);
    setTemplateName(details.name);
    setLabels(details.labels);
    setHtml(details.html);
    setCss(details.css);
    setAdditionalSettings(details.additionalInfo);
  }, []);

  /**
   * One request per selected range: the tabs are served by the same endpoint
   * with different start_date / end_date values. Keyed on the range values
   * rather than the filters object so that changing "Modified by" — which is
   * applied in the browser — does not trigger another request.
   */
  const dateRange = useMemo(
    () => rangeToDates(filters),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filters.range, filters.customFrom, filters.customTo],
  );

  const loadTemplate = useCallback(async () => {
    if (!oauthTokenHeader || !templateSharedUid) return;

    const isFirstLoad = !initialLoadDone.current;
    if (isFirstLoad) setLoading(true);
    else setRevisionsLoading(true);

    let failed = false;
    await DesignTemplatesEndPointLogic.getSingleDesignTemplate(
      oauthTokenHeader,
      templateSharedUid,
      (raw: any) => applyTemplate(mapTemplateDetails(raw)),
      () => {
        failed = true;
      },
      dateRange,
    );

    initialLoadDone.current = true;
    setLoading(false);
    setRevisionsLoading(false);

    // Only bail out of the screen if the very first load failed.
    if (failed && isFirstLoad) {
      const params = new URLSearchParams(location.search);
      params.delete("templateSharedUid");
      navigate(`/operations/templates?${params.toString()}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applyTemplate, oauthTokenHeader, templateSharedUid, dateRange]);

  useEffect(() => {
    loadTemplate();
  }, [loadTemplate]);

  useEffect(() => {
    if (!oauthTokenHeader) return;

    DesignTemplatesEndPointLogic.getLabels(
      oauthTokenHeader,
      (content: any[]) => {
        const unique = Array.from(
          new Set(
            (content ?? []).map((item: any) => item?.label).filter(Boolean),
          ),
        );
        setLabelOptions(unique as string[]);
      },
      () => undefined,
    );
  }, [oauthTokenHeader]);

  const relatedWebsite = useMemo(() => {
    const match = (siteOptions ?? []).find(
      (option: any) => option?.uid === websiteSharedUid,
    );
    return match?.siteName ?? "";
  }, [siteOptions, websiteSharedUid]);

  const editors = useMemo(() => {
    const all = revisions.map((revision) => revision.modifiedBy);
    return Array.from(new Set(all.filter(Boolean)));
  }, [revisions]);

  const visibleRevisions = useMemo(
    () => filterRevisions(revisions, filters),
    [revisions, filters],
  );

  const currentContent: RevisionContent = useMemo(
    () => ({
      html: template?.html ?? "",
      css: template?.css ?? "",
      additionalInfo: template?.additionalInfo ?? "{}",
      labels: template?.labels ?? [],
      componentSharedUid: template?.componentSharedUid ?? "",
    }),
    [template],
  );

  const currentModifiedAt = template?.revisions?.[0]?.modifiedAt ?? "";

  // Non-blocking variants sync after the template has been saved
  const syncVariants = () =>
    GenericEndPointsLogic.updateVariants({
      oauthTokenHeader,
      websiteSharedUid,
      entityType: VARIANTS_ENTITY_TYPE,
      entitySharedUid: template?.sharedUid,
    });

  const handleViewRevision = (revision: TemplateRevision) => {
    setActiveRevision(revision);
    setModalOpen(true);
  };

  /**
   * Confirmed restore: writes the revision back over the template through the
   * same PUT the Save Changes button uses. Stays on this screen afterwards and
   * reloads so the form and the revision list reflect what was saved.
   */
  const handleUseVersion = async (
    _revision: TemplateRevision,
    content: RevisionContent,
  ): Promise<boolean> => {
    if (!template) return false;

    const saved = await DesignTemplatesEndPointLogic.editDesignTemplates(
      oauthTokenHeader,
      toEditPayload({
        // The template keeps its saved name and component; the revision
        // supplies the content being restored.
        name: template.name.trim(),
        componentSharedUid:
          content.componentSharedUid || template.componentSharedUid,
        html: content.html,
        css: content.css,
        additionalInfo: content.additionalInfo,
        labels: content.labels,
      }),
      template.sharedUid,
    );

    // The logic layer has already surfaced the error snackbar.
    if (!saved) return false;

    syncVariants();

    setModalOpen(false);
    setToast({
      message: "Template has been updated successfully",
      severity: "success",
    });

    // Re-hydrate the form as well, since the saved content has changed.
    formHydrated.current = false;
    await loadTemplate();

    // Bring the restored content into view.
    window.scrollTo({ top: 0, behavior: "smooth" });

    return true;
  };

  const handleSave = async () => {
    if (!template) return;

    const otherTemplateNames = Array.isArray(designTemplatesData)
      ? designTemplatesData
          .filter((item: any) => item?.shared_uid !== template.sharedUid)
          .map((item: any) => item?.name)
          .filter(Boolean)
      : [];

    const problem = validateTemplate({
      templateName,
      componentSharedUid: template.componentSharedUid,
      html,
      css,
      additionalInfo: additionalSettings,
      otherTemplateNames,
    });

    if (problem) {
      setToast({ message: problem, severity: "error" });
      return;
    }

    setSaving(true);
    const saved = await DesignTemplatesEndPointLogic.editDesignTemplates(
      oauthTokenHeader,
      toEditPayload({
        // Validated on the trimmed value, so store it trimmed too.
        name: templateName.trim(),
        componentSharedUid: template.componentSharedUid,
        html,
        css,
        additionalInfo: additionalSettings,
        labels,
      }),
      template.sharedUid,
    );

    if (!saved) {
      // The logic layer has already surfaced the error; stay put so the user
      // can correct and retry without losing anything.
      setSaving(false);
      return;
    }

    syncVariants();

    setToast({
      message: "Template has been updated successfully",
      severity: "success",
    });

    // Stay on this screen and reload, so the form and the revision history
    // reflect what was just saved.
    formHydrated.current = false;
    await loadTemplate();
    setSaving(false);
  };

  const handleFormatHtml = () =>
    setHtml(beautifyHtml(html, { indent_size: 2 }));
  const handleFormatCss = () => setCss(beautifyCss(css, { indent_size: 2 }));
  const handleFormatJson = () => {
    try {
      setAdditionalSettings(
        JSON.stringify(JSON.parse(additionalSettings || "{}"), null, 2),
      );
    } catch {
      setToast({
        message: "Invalid JSON format. Please correct the JSON syntax.",
        severity: "error",
      });
    }
  };

  if (loading) return <SimpleBackdrop />;

  return (
    <Box
      sx={{
        backgroundColor: tokens.bg,
        padding: "24px 32px 40px",
        /* Nothing on the page can be touched while the save is in flight.
           Inputs below are disabled too, so keyboard entry is blocked as
           well as clicks. */
        pointerEvents: saving ? "none" : "auto",
      }}
    >
      {/* Page title with the primary actions aligned to it. */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          flexWrap: "wrap",
          mb: 2.75,
        }}
      >
        <Typography
          component="h1"
          sx={{ fontSize: 24, fontWeight: 700, letterSpacing: "-0.01em" }}
        >
          Edit Template
        </Typography>
        <Box sx={{ display: "flex", gap: 1.25 }}>
          <Button
            onClick={() => navigate(`/operations/templates${location.search}`)}
            disabled={saving}
            data-tracker="1077"
            sx={{
              fontSize: 13,
              fontWeight: 600,
              textTransform: "none",
              color: tokens.accent,
              border: `1px solid ${tokens.accent}`,
              borderRadius: tokens.radius,
              px: 2,
              "&:hover": { backgroundColor: tokens.accentSoft },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={saving}
            onClick={handleSave}
            data-tracker="1078"
            /* The label stays put and the trailing check becomes the spinner,
             so the button does not resize while the save is in flight. */
            endIcon={
              saving ? (
                <CircularProgress size={14} sx={{ color: "#FFFFFF" }} />
              ) : (
                <CheckIcon sx={{ fontSize: 14 }} />
              )
            }
            sx={{
              fontSize: 13,
              fontWeight: 600,
              textTransform: "none",
              borderRadius: tokens.radius,
              px: 2,
              backgroundColor: tokens.accent,
              "&:hover": { backgroundColor: tokens.accentHover },
              // Reads as busy rather than dead while it is disabled.
              "&.Mui-disabled": {
                backgroundColor: tokens.accent,
                color: "#FFFFFF",
                opacity: 0.7,
              },
            }}
          >
            Save Changes
          </Button>
        </Box>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "340px 1fr" },
          gap: 2.5,
          alignItems: "start",
          mb: 2.5,
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
          <GeneralCard
            templateName={templateName}
            onTemplateNameChange={setTemplateName}
            relatedWebsite={relatedWebsite}
            componentType={template?.componentName ?? ""}
            disabled={saving}
          />
          <LabelsCard
            labels={labels}
            options={labelOptions}
            onChange={setLabels}
            disabled={saving}
          />
        </Box>

        <CodeCard
          html={html}
          css={css}
          onHtmlChange={setHtml}
          onCssChange={setCss}
          onFormatHtml={handleFormatHtml}
          onFormatCss={handleFormatCss}
          disabled={saving}
        />
      </Box>

      <Box
        sx={{
          display: "grid",
          // Settings editor stays marginally the wider of the two.
          gridTemplateColumns: { xs: "1fr", md: "5fr 4fr" },
          gap: 2.5,
          /* Stretch, so a collapsed Additional Settings card grows to the
             caution note's height. The note itself opts out below, so it does
             not stretch when the settings card is the taller of the two. */
          alignItems: "stretch",
          mb: 2.5,
        }}
      >
        <AdditionalSettingsCard
          value={additionalSettings}
          onChange={setAdditionalSettings}
          onFormat={handleFormatJson}
          disabled={saving}
        />
        <CautionNote />
      </Box>

      <Box sx={{ mb: 2.5 }}>
        <RevisionHistoryCard
          revisions={visibleRevisions}
          loading={revisionsLoading}
          filters={filters}
          editors={editors}
          onFiltersChange={setFilters}
          onViewRevision={handleViewRevision}
        />
      </Box>

      <RevisionDetailModal
        open={modalOpen}
        revision={activeRevision}
        templateSharedUid={templateSharedUid}
        currentContent={currentContent}
        currentModifiedAt={currentModifiedAt}
        onClose={() => setModalOpen(false)}
        onUseVersion={handleUseVersion}
      />

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={4200}
        onClose={() => setToast(null)}
        /* Matches the app-level snackbar in ErrorMessage.tsx, so messages from
           this screen always appear in the same place. */
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={toast?.severity ?? "success"}
          variant="filled"
          onClose={() => setToast(null)}
          sx={{ fontSize: 13 }}
        >
          {toast?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EditTemplateRevision;
